import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const field = "w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition";

export default function GuardianVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("working");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (!token) return setStatus("error");
    api
      .post("/auth/verify-email", { token })
      .then(() => setStatus("ready"))
      .catch((err) => {
        toast.error(errMsg(err));
        setStatus("error");
      });
  }, [token]);

  const setup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/guardian/set-password", { token, password });
      signIn(data.token, data.user);
      toast.success("Guardian dashboard unlocked");
      navigate("/guardian");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-12">
      <div className="relative z-10 w-full max-w-md reveal" data-testid="guardian-verify-panel">
        <div className="mb-6"><Logo size="md" testId="guardian-verify-logo" /></div>
        <p className="rule-label">Guardian verification</p>
        <h1 className="mt-4 text-3xl font-extrabold">
          {status === "error" ? "This link is not valid" : "You're the approver"}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {status === "error"
            ? "The verification link is invalid or already used. Ask the student to resend it from their profile."
            : "Email verified. Set a password to reach your dashboard, where you approve the curriculum and every later change."}
        </p>
        {status === "ready" && (
          <form onSubmit={setup} className="mt-8 card-flat p-7 space-y-4">
            <div>
              <label className="rule-label">Create a password</label>
              <input data-testid="guardian-password" type="password" minLength={6} className={`${field} mt-2`} value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button data-testid="guardian-verify-submit" disabled={busy} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {busy ? "Setting up…" : "Open guardian dashboard"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
